import { Link } from "react-router-dom";
import { Instagram, Facebook, Mail, Flame } from "lucide-react";
import { useState } from "react";
import { subscribeNewsletter } from "../lib/api";
import { toast } from "sonner";
import Nix18Logo from "./Nix18Logo";

const TikTokIcon = ({ size = 16 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M16.6 5.82a4.28 4.28 0 0 1-1.05-2.82h-3.1v12.36a2.6 2.6 0 1 1-2.6-2.6c.27 0 .53.04.78.12V9.7a5.7 5.7 0 0 0-.78-.05 5.78 5.78 0 1 0 5.78 5.78V9.18a7.36 7.36 0 0 0 4.3 1.38V7.5a4.32 4.32 0 0 1-3.33-1.68Z" />
  </svg>
);

const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await subscribeNewsletter(email);
      toast.success(res.message || "Aangemeld!");
      setEmail("");
    } catch {
      toast.error("Aanmelden mislukt. Probeer opnieuw.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer
      data-testid="footer"
      className="relative mt-32 border-t border-[var(--tha-border)] bg-[#070707]"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="font-display text-5xl text-white leading-none">
            TIK HEM <span className="neon-red-text">AAN!</span>
          </div>
          <p className="mt-5 text-[var(--tha-muted)] max-w-sm text-sm leading-relaxed">
            Familieavond werd familielegende. Nu schenken we het door — één kleine fles,
            één grote tik.
          </p>
          <div className="mt-6 flex items-center gap-3">
            <a
              data-testid="footer-tiktok"
              href="https://www.tiktok.com/@tikhemaanshot"
              target="_blank"
              rel="noreferrer"
              className="btn-ghost-red p-3"
              aria-label="TikTok"
            >
              <TikTokIcon size={16} />
            </a>
            <a
              data-testid="footer-instagram"
              href="https://www.instagram.com/tikhemaannl"
              target="_blank"
              rel="noreferrer"
              className="btn-ghost-red p-3"
              aria-label="Instagram"
            >
              <Instagram size={16} />
            </a>
            <a
              data-testid="footer-facebook"
              href="https://www.facebook.com/profile.php?id=61589678580761"
              target="_blank"
              rel="noreferrer"
              className="btn-ghost-red p-3"
              aria-label="Facebook"
            >
              <Facebook size={16} />
            </a>
            <a
              data-testid="footer-mail"
              href="mailto:tikhemaannl@gmail.com"
              className="btn-ghost-red p-3"
              aria-label="E-mail"
            >
              <Mail size={16} />
            </a>
          </div>
        </div>

        <div className="md:col-span-3">
          <div className="text-xs font-mono-x tracking-widest text-[var(--tha-muted)]">
            NAVIGATIE
          </div>
          <ul className="mt-5 space-y-3 text-sm">
            <li>
              <Link to="/" className="hover:text-[var(--tha-red)] transition-colors">
                Home
              </Link>
            </li>
            <li>
              <Link to="/verhaal" className="hover:text-[var(--tha-red)] transition-colors">
                Het Verhaal
              </Link>
            </li>
            <li>
              <Link to="/shop" className="hover:text-[var(--tha-red)] transition-colors">
                Shop
              </Link>
            </li>
            <li>
              <Link to="/zakelijk" className="hover:text-[var(--tha-red)] transition-colors">
                Zakelijk
              </Link>
            </li>
            <li>
              <Link to="/merchandise" className="hover:text-[var(--tha-red)] transition-colors">
                Merch
              </Link>
            </li>
            <li>
              <Link to="/contact" className="hover:text-[var(--tha-red)] transition-colors">
                Contact
              </Link>
            </li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <div className="text-xs font-mono-x tracking-widest text-[var(--tha-muted)]">
            NIEUWSBRIEF
          </div>
          <p className="mt-3 text-sm text-[var(--tha-muted)]">
            Schrijf je in voor drops, events en bizar goede deals.
          </p>
          <form onSubmit={submit} className="mt-5 flex" data-testid="newsletter-form">
            <input
              data-testid="newsletter-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="jouw@email.nl"
              className="flex-1 px-4 py-3 text-sm"
            />
            <button
              data-testid="newsletter-submit-button"
              disabled={loading}
              className="btn-red px-5 py-3 font-display tracking-wide text-sm disabled:opacity-60"
            >
              {loading ? "..." : "MELD AAN"}
            </button>
          </form>
        </div>
      </div>

      {/* Legal / responsible drinking band */}
      <div className="border-t border-[var(--tha-border)] bg-[#050505]">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-3 flex md:justify-start justify-center">
              <a
                href="https://www.nix18.nl"
                target="_blank"
                rel="noreferrer"
                data-testid="footer-nix18-logo"
                className="text-[var(--tha-red)] hover:text-white transition-colors"
                aria-label="NIX18 — Geen 18, geen alcohol (officiële site)"
                title="NIX18 — Geen 18, geen alcohol"
              >
                <Nix18Logo size={96} />
              </a>
            </div>
            <div className="md:col-span-9 space-y-3 text-sm text-[var(--tha-muted)] leading-relaxed">
              <p className="text-white font-display text-xl tracking-wide">
                GEEN 18 — GEEN ALCOHOL.
              </p>
              <p>
                Tik Hem Aan! verkoopt geen alcohol aan personen onder de 18 jaar. Dit is
                wettelijk verplicht onder de Nederlandse Alcoholwet (artikel 20).
                Bij elke bestelling bevestig je 18+ te zijn en de bezorger mag bij
                aflevering om geldige legitimatie vragen.
              </p>
              <p>
                Alcohol is een genotsmiddel. Drink met mate, schenk nooit aan
                minderjarigen, niet tijdens zwangerschap, medicijngebruik of in het
                verkeer. Meer informatie en hulp bij problematisch drankgebruik:{" "}
                <a
                  href="https://www.alcoholinfo.nl"
                  target="_blank"
                  rel="noreferrer"
                  className="text-[var(--tha-red)] underline"
                >
                  alcoholinfo.nl
                </a>
                .
              </p>
              <div className="flex flex-wrap gap-x-5 gap-y-2 pt-2 text-xs font-mono-x tracking-widest">
                <Link
                  to="/voorwaarden"
                  data-testid="footer-voorwaarden-link"
                  className="hover:text-white transition-colors"
                >
                  ALGEMENE VOORWAARDEN
                </Link>
                <Link
                  to="/privacy"
                  data-testid="footer-privacy-link"
                  className="hover:text-white transition-colors"
                >
                  PRIVACYVERKLARING
                </Link>
                <a
                  href="https://ec.europa.eu/consumers/odr"
                  target="_blank"
                  rel="noreferrer"
                  className="hover:text-white transition-colors"
                >
                  EU ODR-PLATFORM
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-[var(--tha-border)]">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-[var(--tha-dim)] font-mono-x tracking-widest">
          <div className="flex items-center gap-2 text-center sm:text-left">
            <Flame size={12} className="text-[var(--tha-red)]" />© {new Date().getFullYear()} TIK HEM AAN! — KVK 42065864 — BTW NL
          </div>
          <div className="flex items-center gap-4 flex-wrap justify-center">
            <span>NIX18</span>
            <span>·</span>
            <span>VERANTWOORD VERKOPEN</span>
            <span>·</span>
            <span>BTW INBEGREPEN</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
